import { styled } from '@linaria/react';
import { Stack } from '@mantine/core';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useOutcomes } from '../../../api/strategy-client/strategy-client.hooks';
import type { Outcome } from '../../../api/strategy-client/strategy-client.type';
import { useTranslation } from 'react-i18next';
import { FilterPanel } from '../../../components/filters/filter-panel';
import { AffectedItemsSidebar } from '../../../components/insights/affected-items-sidebar';
import { BrandedLoadingOverlay } from '../../../components/loader/branded-loader';
import { PageLayout } from '../../../components/page-layout/page-layout';
import { RiskAnalysisInitiativeSideSection } from '../../../components/side-section/risk-analysis-initiative-side-section';
import { RiskAnalysisOutcomeSideSection } from '../../../components/side-section/risk-analysis-outcome-side-section';
import { RiskAnalysisPullRequestSideSection } from '../../../components/side-section/risk-analysis-pull-request-side-section';
import { RiskAnalysisTaskSideSection } from '../../../components/side-section/risk-analysis-task-side-section';
import { useFeatureFlag } from '../../../helpers/feature-flags/feature-flags.hooks';
import { FeatureFlag } from '../../../helpers/feature-flags/feature-flags.type';
import { useAdherenceStore } from '../../../store/adherence-store/adherence-store';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { getFilterGroups, useFilteredData } from './filters/risk-analysis-filter-groups.helpers';
import { useRiskAnalysisFilters } from './filters/risk-analysis-filters.hooks';
import { RiskAnalysisInitiativeOverview } from './initiatives/risk-analysis-initiative-overview';
import { RiskAnalysisInitiativeTable } from './initiatives/risk-analysis-initiative-table';
import { RiskAnalysisOutcomesOverview } from './outcomes/risk-analysis-outcomes-overview';
import { RiskAnalysisOutcomesTable } from './outcomes/risk-analysis-outcomes-table';
import { RiskAnalysisPullRequestOverview } from './pull-requests/risk-analysis-pull-request-overview';
import { RiskAnalysisPullRequestTable } from './pull-requests/risk-analysis-pull-request-table';
import {
  RiskAnalysisInitiative,
  RiskAnalysisPullRequest,
  RiskAnalysisSection,
  RiskAnalysisTask,
} from './risk-analysis.client.type';
import { getRiskCategory } from './risk-analysis.helpers';
import {
  useRiskAnalysisBloomfilterStages,
  useRiskAnalysisCreators,
  useRiskAnalysisEpics,
  useRiskAnalysisEpicsFilterOptions,
  useRiskAnalysisInitiatives,
  useRiskAnalysisInitiativesOptions,
  useRiskAnalysisProjectOptions,
  useRiskAnalysisPullRequests,
  useRiskAnalysisRepositoryOptions,
  useRiskAnalysisRiskCategories,
  useRiskAnalysisTaskTypes,
  useRiskAnalysisTasks,
} from './risk-analysis.hooks';
import { RiskAnalysisTaskOverview } from './tasks/risk-analysis-task-overview';
import { RiskAnalysisTaskTable } from './tasks/risk-analysis-task-table';

export function RiskAnalysis() {
  const i18n = useTranslation();
  const useAI = useGlobalStore((state) => state.useAI);
  const portfolio = useGlobalStore((state) => state.portfolio);
  const selectedSection = useAdherenceStore((state) => state.selectedRiskAnalysisSection);
  const setSelectedSection = useAdherenceStore((state) => state.setSelectedRiskAnalysisSection);
  const [selectedTask, setSelectedTask] = useState<RiskAnalysisTask | null>(null);
  const [selectedInitiative, setSelectedInitiative] = useState<RiskAnalysisInitiative | null>(null);
  const [selectedPullRequest, setSelectedPullRequest] = useState<RiskAnalysisPullRequest | null>(null);
  const [selectedOutcome, setSelectedOutcome] = useState<Outcome | null>(null);
  const [selectedInsightId, setSelectedInsightId] = useState<string | null>(null);
  const [taskViewTab, setTaskViewTab] = useState<'table' | 'flora'>('table');
  const [epicViewTab, setEpicViewTab] = useState<'table' | 'flora'>('table');

  const { data: tasks, isFetching: tasksIsFetching } = useRiskAnalysisTasks(portfolio?.id ?? '');
  const { data: initiatives, isFetching: initiativesIsFetching } = useRiskAnalysisInitiatives(portfolio?.id ?? '');
  const { data: epics, isFetching: epicsIsFetching } = useRiskAnalysisEpics(portfolio?.id ?? '');
  const { data: pullRequests, isFetching: pullRequestsIsFetching } = useRiskAnalysisPullRequests(portfolio?.id ?? '');

  const isCorporateGoalsEnabled = useFeatureFlag(FeatureFlag.CorporateGoals);
  const isRiskInsightsEnabled = useFeatureFlag(FeatureFlag.RiskInsights);

  const { outcomes, query: outcomesQuery } = useOutcomes({
    enabled: selectedSection === RiskAnalysisSection.OUTCOMES && isCorporateGoalsEnabled,
  });
  const outcomesIsFetching = outcomesQuery.isFetching;

  // Automatically switch to TASKS section when organization changes and corporate goals are disabled
  useEffect(() => {
    if (!isCorporateGoalsEnabled && selectedSection === RiskAnalysisSection.OUTCOMES) {
      setSelectedSection(RiskAnalysisSection.TASKS);
    }
  }, [isCorporateGoalsEnabled, selectedSection, setSelectedSection]);

  const taskTypes = useRiskAnalysisTaskTypes(tasks ?? []);
  const bloomfilterStageOptions = useRiskAnalysisBloomfilterStages(tasks ?? []);
  const epicsFilterOptions = useRiskAnalysisEpicsFilterOptions(tasks ?? []);
  const initiativesFilterOptions = useRiskAnalysisInitiativesOptions(tasks ?? []);
  const creators = useRiskAnalysisCreators(tasks ?? []);
  const projectOptions = useRiskAnalysisProjectOptions(tasks ?? []);
  const epicProjectOptions = useRiskAnalysisProjectOptions(epics ?? []);
  const taskRiskCategories = useRiskAnalysisRiskCategories(tasks ?? []);
  const epicRiskCategories = useRiskAnalysisRiskCategories(epics ?? []);
  const initiativeFilterOptionsForEpic = useRiskAnalysisInitiativesOptions(epics ?? []);
  const repositoryOptions = useRiskAnalysisRepositoryOptions(pullRequests ?? []);

  // Combine risk categories from both tasks and outcomes
  const riskCategories = useMemo(() => {
    const allCategories = new Set<string>();

    // Add categories from tasks
    taskRiskCategories.forEach((cat) => {
      allCategories.add(cat.value);
    });

    // Add categories from outcomes
    (outcomes ?? []).forEach((outcome) => {
      const category = getRiskCategory(outcome.flora_annotations?.risk_score).label;
      allCategories.add(category);
    });

    return Array.from(allCategories)
      .sort()
      .map((category) => ({
        value: category,
        label: category,
      }));
  }, [taskRiskCategories, outcomes]);

  const filters = useRiskAnalysisFilters(
    creators,
    projectOptions,
    taskTypes,
    riskCategories,
    repositoryOptions,
    epicProjectOptions,
    epicRiskCategories,
    bloomfilterStageOptions,
  );
  // Track initialization state for each section to prevent re-initialization when user clears filters
  const filtersInitializedRef = useRef<Record<string, boolean>>({
    [RiskAnalysisSection.TASKS]: false,
    [RiskAnalysisSection.EPICS]: false,
    [RiskAnalysisSection.INITIATIVES]: false,
    [RiskAnalysisSection.PULL_REQUESTS]: false,
    [RiskAnalysisSection.OUTCOMES]: false,
  });

  // Filter values and selected entities are portfolio-specific (project
  // names, source-system work item types, task/initiative/PR ids), so a
  // portfolio switch without unmounting must drop the stale state. Filters
  // are CLEARED rather than re-seeded here so the one-time initialization
  // effect below performs the seeding once the new portfolio's data arrives
  // and keeps filtersInitializedRef consistent with what it seeded.
  const seededPortfolioIdRef = useRef(portfolio?.id);
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset must run only when the portfolio identity changes
  useEffect(() => {
    if (seededPortfolioIdRef.current === portfolio?.id) {
      return;
    }
    seededPortfolioIdRef.current = portfolio?.id;
    filtersInitializedRef.current = {
      [RiskAnalysisSection.TASKS]: false,
      [RiskAnalysisSection.EPICS]: false,
      [RiskAnalysisSection.INITIATIVES]: false,
      [RiskAnalysisSection.PULL_REQUESTS]: false,
      [RiskAnalysisSection.OUTCOMES]: false,
    };
    setSelectedTask(null);
    setSelectedInitiative(null);
    setSelectedPullRequest(null);
    setSelectedOutcome(null);
    setSelectedInsightId(null);
    filters.handleClearAllFilters();
  }, [portfolio?.id]);

  // Initialize filters for the current section when data is available
  // This runs ONCE per section, not on every filter change
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally using specific filter properties to avoid re-running on every filter object change
  useEffect(() => {
    // Skip if this section's filters have already been initialized
    if (filtersInitializedRef.current[selectedSection]) return;

    let shouldInitialize = false;
    let hasRequiredData = false;

    switch (selectedSection) {
      case RiskAnalysisSection.TASKS:
        hasRequiredData = (tasks?.length ?? 0) > 0 && projectOptions.length > 0;
        shouldInitialize =
          hasRequiredData &&
          filters.creatorFilters.length === 0 &&
          filters.projectFilters.length === 0 &&
          filters.typeFilters.length === 0 &&
          filters.riskCategoryFilters.length === 0 &&
          filters.bloomfilterStageFilters.length === 0;
        break;

      case RiskAnalysisSection.EPICS:
        hasRequiredData = (epics?.length ?? 0) > 0 && epicProjectOptions.length > 0;
        shouldInitialize =
          hasRequiredData && filters.epicProjectFilters.length === 0 && filters.epicRiskCategoryFilters.length === 0;
        break;

      case RiskAnalysisSection.INITIATIVES:
        hasRequiredData = (initiatives?.length ?? 0) > 0;
        shouldInitialize = hasRequiredData && filters.initiativeRiskCategoryFilters.length === 0;
        break;

      case RiskAnalysisSection.PULL_REQUESTS:
        hasRequiredData = (pullRequests?.length ?? 0) > 0 && repositoryOptions.length > 0;
        shouldInitialize =
          hasRequiredData && filters.prRepositoryFilters.length === 0 && filters.prRiskCategoryFilters.length === 0;
        break;

      case RiskAnalysisSection.OUTCOMES:
        hasRequiredData = (outcomes?.length ?? 0) > 0;
        shouldInitialize = hasRequiredData && filters.objectiveRiskCategoryFilters.length === 0;
        break;
    }

    if (shouldInitialize) {
      filters.handleResetFilters(selectedSection);
      filtersInitializedRef.current[selectedSection] = true;
    }
  }, [
    selectedSection,
    tasks,
    epics,
    initiatives,
    pullRequests,
    outcomes,
    creators,
    projectOptions,
    epicProjectOptions,
    repositoryOptions,
    filters.creatorFilters.length,
    filters.projectFilters.length,
    filters.typeFilters.length,
    filters.riskCategoryFilters.length,
    filters.bloomfilterStageFilters.length,
    filters.epicProjectFilters.length,
    filters.epicRiskCategoryFilters.length,
    filters.initiativeRiskCategoryFilters.length,
    filters.prRepositoryFilters.length,
    filters.prRiskCategoryFilters.length,
    filters.objectiveRiskCategoryFilters.length,
  ]);

  const { filteredTasks, filteredInitiatives, filteredEpics, filteredPullRequests, filteredOutcomes } = useFilteredData(
    tasks,
    initiatives,
    epics,
    pullRequests,
    outcomes,
    filters,
  );

  const handleSideSectionClose = () => {
    setSelectedTask(null);
    setSelectedInitiative(null);
    setSelectedPullRequest(null);
    setSelectedOutcome(null);
    setSelectedInsightId(null);
  };

  const currentActiveTab =
    selectedSection === RiskAnalysisSection.TASKS
      ? taskViewTab
      : selectedSection === RiskAnalysisSection.EPICS
        ? epicViewTab
        : undefined;

  const filterGroups = getFilterGroups(
    selectedSection,
    {
      projectOptions,
      epicProjectOptions,
      taskTypes,
      riskCategories,
      epicRiskCategories,
      creators,
      initiativesFilterOptions,
      epicsFilterOptions,
      initiativeFilterOptionsForEpic,
      repositoryOptions,
      bloomfilterStageOptions,
      filters,
      activeTab: currentActiveTab,
    },
    i18n.t,
  );

  const isLoading = () => {
    switch (selectedSection) {
      case RiskAnalysisSection.TASKS:
        return tasksIsFetching;
      case RiskAnalysisSection.INITIATIVES:
        return initiativesIsFetching;
      case RiskAnalysisSection.EPICS:
        return epicsIsFetching;
      case RiskAnalysisSection.PULL_REQUESTS:
        return pullRequestsIsFetching;
      case RiskAnalysisSection.OUTCOMES:
        return outcomesIsFetching;
      default:
        return false;
    }
  };

  // Filter out OBJECTIVES section if feature flag is not enabled
  const availableSections = useMemo(() => {
    let sections = [
      { value: RiskAnalysisSection.TASKS, label: i18n.t('common.work_units') },
      { value: RiskAnalysisSection.INITIATIVES, label: i18n.t('common.initiatives') },
      { value: RiskAnalysisSection.EPICS, label: `${i18n.t('common.parent_task')}s` },
      { value: RiskAnalysisSection.PULL_REQUESTS, label: i18n.t('common.pull_requests') },
    ];

    if (isCorporateGoalsEnabled) {
      sections.push({ value: RiskAnalysisSection.OUTCOMES, label: i18n.t('common.outcomes') });
    }

    return sections;
  }, [isCorporateGoalsEnabled, i18n.t]);

  return (
    <Fragment>
      <BrandedLoadingOverlay visible={isLoading()} variant="colored" />
      {useAI && (
        <PageLayout
          controlBar={null}
          overview={{
            title: 'Risk Overview',
            content: (() => {
              switch (selectedSection) {
                case RiskAnalysisSection.TASKS:
                  return <RiskAnalysisTaskOverview tasks={filteredTasks} />;
                case RiskAnalysisSection.INITIATIVES:
                  return <RiskAnalysisInitiativeOverview initiatives={filteredInitiatives} />;
                case RiskAnalysisSection.EPICS:
                  return <RiskAnalysisTaskOverview tasks={filteredEpics} isEpic={true} />;
                case RiskAnalysisSection.PULL_REQUESTS:
                  return <RiskAnalysisPullRequestOverview pullRequests={filteredPullRequests} />;
                case RiskAnalysisSection.OUTCOMES:
                  return <RiskAnalysisOutcomesOverview outcomes={filteredOutcomes} />;
                default:
                  return null;
              }
            })(),
          }}
          sideFilters={{
            content: (
              <Stack gap="md">
                <FilterPanel
                  // Hide section selector when Flora tab is active
                  selectedOption={currentActiveTab === 'flora' ? undefined : selectedSection}
                  options={currentActiveTab === 'flora' ? undefined : availableSections}
                  onOptionChange={
                    currentActiveTab === 'flora'
                      ? undefined
                      : (value) => {
                          setSelectedSection(value as RiskAnalysisSection);
                          filters.handleResetFilters(value as RiskAnalysisSection);
                          handleSideSectionClose();
                        }
                  }
                  filterGroups={filterGroups}
                  onResetFilters={() => {
                    if (currentActiveTab === 'flora') {
                      // Reset Flora filters to defaults (all options selected)
                      filters.setFloraInsightTypeFilters(['task', 'epic', 'initiative', 'pull_request']);
                      filters.setFloraSeverityFilters(['high', 'medium', 'low']);
                      filters.setFloraStatusFilters(['not_started', 'in_progress', 'completed']);
                      filters.setFloraViewFilter('active');
                    } else {
                      filters.handleResetFilters(selectedSection);
                    }
                  }}
                />
              </Stack>
            ),
          }}
          mainSection={(() => {
            switch (selectedSection) {
              case RiskAnalysisSection.TASKS:
                return (
                  <RiskAnalysisTaskTable
                    tasks={filteredTasks}
                    onTaskClick={setSelectedTask}
                    activeTab={taskViewTab}
                    onActiveTabChange={setTaskViewTab}
                    floraInsightTypeFilters={filters.floraInsightTypeFilters}
                    floraSeverityFilters={filters.floraSeverityFilters}
                    floraStatusFilters={filters.floraStatusFilters}
                    floraViewFilter={filters.floraViewFilter}
                    onInsightClick={setSelectedInsightId}
                    showInsightsTab={isRiskInsightsEnabled}
                  />
                );
              case RiskAnalysisSection.INITIATIVES:
                return (
                  <RiskAnalysisInitiativeTable
                    initiatives={filteredInitiatives}
                    onInitiativeClick={setSelectedInitiative}
                  />
                );
              case RiskAnalysisSection.EPICS:
                return (
                  <RiskAnalysisTaskTable
                    tasks={filteredEpics}
                    onTaskClick={setSelectedTask}
                    isEpic={true}
                    activeTab={epicViewTab}
                    onActiveTabChange={setEpicViewTab}
                    floraInsightTypeFilters={filters.floraInsightTypeFilters}
                    floraSeverityFilters={filters.floraSeverityFilters}
                    floraStatusFilters={filters.floraStatusFilters}
                    floraViewFilter={filters.floraViewFilter}
                    onInsightClick={setSelectedInsightId}
                    showInsightsTab={isRiskInsightsEnabled}
                  />
                );
              case RiskAnalysisSection.PULL_REQUESTS:
                return (
                  <RiskAnalysisPullRequestTable
                    pullRequests={filteredPullRequests}
                    onPullRequestClick={setSelectedPullRequest}
                  />
                );
              case RiskAnalysisSection.OUTCOMES:
                return <RiskAnalysisOutcomesTable outcomes={filteredOutcomes} onOutcomeClick={setSelectedOutcome} />;
              default:
                return null;
            }
          })()}
          sideSection={(() => {
            // Show affected items sidebar if an insight is selected
            if (selectedInsightId) {
              return <AffectedItemsSidebar insightId={selectedInsightId} onClose={handleSideSectionClose} />;
            }

            // Otherwise show the regular side sections
            switch (selectedSection) {
              case RiskAnalysisSection.TASKS:
                return <RiskAnalysisTaskSideSection task={selectedTask} onClose={handleSideSectionClose} />;
              case RiskAnalysisSection.INITIATIVES:
                return (
                  <RiskAnalysisInitiativeSideSection initiative={selectedInitiative} onClose={handleSideSectionClose} />
                );
              case RiskAnalysisSection.EPICS:
                return <RiskAnalysisTaskSideSection task={selectedTask} onClose={handleSideSectionClose} />;
              case RiskAnalysisSection.PULL_REQUESTS:
                return (
                  <RiskAnalysisPullRequestSideSection
                    pullRequest={selectedPullRequest}
                    onClose={handleSideSectionClose}
                  />
                );
              case RiskAnalysisSection.OUTCOMES:
                return <RiskAnalysisOutcomeSideSection outcome={selectedOutcome} onClose={handleSideSectionClose} />;
              default:
                return null;
            }
          })()}
          showSideSection={
            selectedTask !== null ||
            selectedInitiative !== null ||
            selectedPullRequest !== null ||
            selectedOutcome !== null ||
            selectedInsightId !== null
          }
        />
      )}
    </Fragment>
  );
}

export const RiskBadge = styled.span<{ color: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${(props) => `${props.color}15`};
  color: ${(props) => props.color};
  border: 1px solid ${(props) => `${props.color}30`};
`;
